import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Progress } from './ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  ArrowLeft, ClipboardCheck, Plus, BarChart3, Target, Zap, TrendingUp,
  ChevronDown, ChevronUp, Eye, Star, DollarSign, Calculator, CheckCircle, Globe, Lightbulb, Leaf, FileDown, Archive
} from 'lucide-react';
import { toast } from 'sonner';
import TheoryOfChange from './TheoryOfChange';
import EvaluationBenchmark from './EvaluationBenchmark';

const API = BACKEND_URL;

const STAGE_CONFIG = {
  design: { color: 'bg-blue-100 text-blue-800', label: 'Design Phase' },
  implementation: { color: 'bg-amber-100 text-amber-800', label: 'Implementation Phase' },
  completion: { color: 'bg-green-100 text-green-800', label: 'Completion Phase' },
};

const DIM_ICONS = {
  social_footprint: Target,
  extroversion: Globe,
  innovation: Lightbulb,
  sustainability: Leaf,
};

const InvestmentEvaluation = ({ user }) => {
  const navigate = useNavigate();
  const [evaluations, setEvaluations] = useState([]);
  const [stats, setStats] = useState({});
  const [dimensions, setDimensions] = useState([]);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [investments, setInvestments] = useState([]);
  const [selectedEval, setSelectedEval] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showScoringDialog, setShowScoringDialog] = useState(false);
  const [showSROIDialog, setShowSROIDialog] = useState(false);
  const [newEval, setNewEval] = useState({ investment_id: '', investment_title: '', stage: 'design', notes: '' });
  const [pendingScores, setPendingScores] = useState({});
  const [sroiForm, setSroiForm] = useState({
    inputs_description: '', inputs_cost: '',
    outcomes: [{ name: '', description: '', quantity: '', financial_proxy: '', value_per_unit: '' }],
    deadweight_pct: '10', attribution_pct: '90',
  });
  const [stageFilter, setStageFilter] = useState('all');
  const [mainTab, setMainTab] = useState('evaluations');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [evalRes, dimRes, invRes] = await Promise.all([
        axios.get(`${API}/api/investment-evaluation/evaluations`, { headers }),
        axios.get(`${API}/api/investment-evaluation/dimensions`, { headers }),
        axios.get(`${API}/api/social-investments`, { headers }).catch(() => ({ data: { investments: [] } })),
      ]);
      setEvaluations(evalRes.data.evaluations || []);
      setStats(evalRes.data.stats || {});
      setDimensions(dimRes.data.dimensions || []);
      setStages(dimRes.data.stages || []);
      setInvestments(invRes.data.investments || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSeed = async () => {
    try {
      await axios.post(`${API}/api/investment-evaluation/seed`, {}, { headers });
      toast.success('Sample evaluation data seeded');
      fetchAll();
    } catch (error) { toast.error('Failed to seed'); }
  };

  const handleCreate = async () => {
    if (!newEval.investment_title) { toast.error('Investment title is required'); return; }
    try {
      const inv = investments.find(i => i.id === newEval.investment_id);
      await axios.post(`${API}/api/investment-evaluation/evaluations`, {
        ...newEval,
        investment_title: inv?.title || newEval.investment_title,
      }, { headers });
      toast.success('Evaluation created');
      setShowCreateDialog(false);
      setNewEval({ investment_id: '', investment_title: '', stage: 'design', notes: '' });
      fetchAll();
    } catch (error) { toast.error('Failed to create evaluation'); }
  };

  const handleViewEval = async (evalId) => {
    try {
      const res = await axios.get(`${API}/api/investment-evaluation/evaluations/${evalId}`, { headers });
      setSelectedEval(res.data);
    } catch (error) { toast.error('Failed to load evaluation'); }
  };

  const openScoring = (eval_data) => {
    setSelectedEval(eval_data);
    const existing = eval_data.scores || {};
    const init = {};
    dimensions.forEach(dim => {
      dim.criteria.forEach(c => {
        init[c.id] = {
          score: existing[c.id]?.score || 0,
          justification: existing[c.id]?.justification || '',
        };
      });
    });
    setPendingScores(init);
    setShowScoringDialog(true);
  };

  const handleSubmitScores = async () => {
    if (!selectedEval) return;
    const scoresToSubmit = Object.entries(pendingScores)
      .filter(([_, v]) => v.score > 0)
      .map(([criterion_id, v]) => ({ criterion_id, score: v.score, justification: v.justification }));
    if (scoresToSubmit.length === 0) { toast.error('Please score at least one criterion'); return; }
    try {
      await axios.post(`${API}/api/investment-evaluation/evaluations/${selectedEval.id}/scores`, scoresToSubmit, { headers });
      toast.success('Scores saved');
      setShowScoringDialog(false);
      fetchAll();
      handleViewEval(selectedEval.id);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save scores'); }
  };

  const handleSubmitSROI = async () => {
    if (!selectedEval || !sroiForm.inputs_cost) { toast.error('Investment cost is required'); return; }
    try {
      await axios.post(`${API}/api/investment-evaluation/evaluations/${selectedEval.id}/sroi`, {
        inputs_description: sroiForm.inputs_description,
        inputs_cost: parseFloat(sroiForm.inputs_cost),
        outcomes: sroiForm.outcomes.filter(o => o.name).map(o => ({
          ...o, quantity: parseFloat(o.quantity) || 0, value_per_unit: parseFloat(o.value_per_unit) || 0,
        })),
        deadweight_pct: parseFloat(sroiForm.deadweight_pct) || 0,
        attribution_pct: parseFloat(sroiForm.attribution_pct) || 100,
      }, { headers });
      toast.success('SROI calculated');
      setShowSROIDialog(false);
      fetchAll();
      handleViewEval(selectedEval.id);
    } catch (error) { toast.error('Failed to calculate SROI'); }
  };

  const handleFinalize = async (evalId) => {
    try {
      await axios.patch(`${API}/api/investment-evaluation/evaluations/${evalId}/status`, { status: 'completed' }, { headers });
      toast.success('Evaluation finalized');
      fetchAll();
      if (selectedEval?.id === evalId) handleViewEval(evalId);
    } catch (error) { toast.error('Failed to finalize'); }
  };

  const handleDownloadReport = async (evalId, title) => {
    try {
      const res = await axios.get(
        `${API}/api/investment-evaluation/evaluations/${evalId}/report.pdf`,
        { headers, responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const safeTitle = (title || 'evaluation').replace(/[^a-zA-Z0-9_-]+/g, '_').slice(0, 60);
      a.href = url;
      a.download = `evaluation_report_${safeTitle}_${evalId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF report downloaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate PDF');
    }
  };

  const handleDownloadInvestmentBundle = async (investmentId, title) => {
    try {
      toast.info('Building bundle… this can take a few seconds.');
      const res = await axios.get(
        `${API}/api/investment-evaluation/investments/${investmentId}/reports.zip`,
        { headers, responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const safeTitle = (title || 'investment').replace(/[^a-zA-Z0-9_-]+/g, '_').slice(0, 60);
      a.href = url;
      a.download = `evaluation_bundle_${safeTitle}_${investmentId.slice(0, 8)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Bundle downloaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to build bundle');
    }
  };

  const addSROIOutcome = () => {
    setSroiForm({ ...sroiForm, outcomes: [...sroiForm.outcomes, { name: '', description: '', quantity: '', financial_proxy: '', value_per_unit: '' }] });
  };

  const updateSROIOutcome = (idx, field, value) => {
    const updated = [...sroiForm.outcomes];
    updated[idx] = { ...updated[idx], [field]: value };
    setSroiForm({ ...sroiForm, outcomes: updated });
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '€0';
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(amount);
  };

  const filtered = evaluations.filter(e => stageFilter === 'all' || e.stage === stageFilter);

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
  }

  // ========== DETAIL VIEW ==========
  if (selectedEval) {
    const dimScores = selectedEval.dimension_scores || {};
    const sroi = selectedEval.sroi;
    return (
      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => setSelectedEval(null)} data-testid="back-to-list-btn">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{selectedEval.investment_title}</h1>
              <div className="flex items-center gap-2 mt-1">
                <Badge className={STAGE_CONFIG[selectedEval.stage]?.color}>{STAGE_CONFIG[selectedEval.stage]?.label}</Badge>
                <Badge variant="outline" className="capitalize">{selectedEval.status}</Badge>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => openScoring(selectedEval)} data-testid="edit-scores-btn"><Star className="h-4 w-4 mr-1" /> Edit Scores</Button>
            <Button size="sm" variant="outline" onClick={() => { setSroiForm({ inputs_description: sroi?.inputs_description || '', inputs_cost: sroi?.inputs_cost || '', outcomes: sroi?.outcomes?.length > 0 ? sroi.outcomes.map(o => ({ ...o, quantity: String(o.quantity), value_per_unit: String(o.value_per_unit) })) : [{ name: '', description: '', quantity: '', financial_proxy: '', value_per_unit: '' }], deadweight_pct: String(sroi?.deadweight_pct || 10), attribution_pct: String(sroi?.attribution_pct || 90) }); setShowSROIDialog(true); }} data-testid="calc-sroi-btn"><Calculator className="h-4 w-4 mr-1" /> SROI</Button>
            <Button size="sm" variant="outline" onClick={() => handleDownloadReport(selectedEval.id, selectedEval.investment_title)} data-testid="download-eval-pdf-btn" className="border-blue-300 text-blue-700 hover:bg-blue-50"><FileDown className="h-4 w-4 mr-1" /> PDF Report</Button>
            <Button size="sm" variant="outline" onClick={() => handleDownloadInvestmentBundle(selectedEval.investment_id, selectedEval.investment_title)} data-testid="download-eval-bundle-btn" className="border-purple-300 text-purple-700 hover:bg-purple-50"><Archive className="h-4 w-4 mr-1" /> Export All (.zip)</Button>
            {selectedEval.status !== 'completed' && <Button size="sm" className="bg-green-700 hover:bg-green-800" onClick={() => handleFinalize(selectedEval.id)} data-testid="finalize-btn"><CheckCircle className="h-4 w-4 mr-1" /> Finalize</Button>}
          </div>
        </div>

        {/* Weighted Total */}
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="p-6 text-center">
            <p className="text-sm text-gray-700 mb-1">Weighted Total Score</p>
            <p className="text-5xl font-bold text-blue-700">{(selectedEval.weighted_total ?? 0).toFixed(2)}<span className="text-lg text-gray-700"> / 5.00</span></p>
            <div className="mt-3 max-w-md mx-auto">
              <Progress value={(selectedEval.weighted_total || 0) / 5 * 100} className="h-3" />
            </div>
            <p className="text-xs text-gray-700 mt-3">
              {(() => {
                const total = (dimensions || []).reduce((sum, d) => sum + (d.criteria || []).length, 0);
                const scored = Object.keys(selectedEval.scores || {}).length;
                const pct = total > 0 ? Math.round((scored / total) * 100) : 0;
                return (
                  <>
                    <span className="font-semibold text-gray-700">{scored}</span>
                    <span> of </span>
                    <span className="font-semibold text-gray-700">{total}</span>
                    <span> criteria scored ({pct}%)</span>
                    {scored === total && total > 0 && <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold">✓ Fully scored</span>}
                  </>
                );
              })()}
            </p>
          </CardContent>
        </Card>

        {/* Dimension Scores */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {dimensions.map(dim => {
            const Icon = DIM_ICONS[dim.id] || Target;
            const score = dimScores[dim.id] || 0;
            return (
              <Card key={dim.id} data-testid={`dim-${dim.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="h-5 w-5" style={{ color: dim.color }} />
                    <span className="text-sm font-medium text-gray-700">{dim.title}</span>
                  </div>
                  <p className="text-xs text-gray-700 mb-2">Weight: {dim.weight * 100}%</p>
                  <div className="flex items-end gap-2">
                    <span className="text-3xl font-bold" style={{ color: dim.color }}>{score ? score.toFixed(2) : '—'}</span>
                    <span className="text-sm text-gray-700 mb-1">/ 5.00</span>
                  </div>
                  <Progress value={(score / 5) * 100} className="h-2 mt-2" />
                  {/* Criteria breakdown */}
                  <div className="mt-3 space-y-1">
                    {dim.criteria.map(c => {
                      const cs = (selectedEval.scores || {})[c.id];
                      return (
                        <div key={c.id} className="flex items-center justify-between text-xs">
                          <span className="text-gray-700 truncate mr-2">{c.title}</span>
                          <div className="flex gap-0.5">
                            {[1, 2, 3, 4, 5].map(n => (
                              <div key={n} className={`w-2 h-2 rounded-full ${cs && n <= cs.score ? 'bg-amber-400' : 'bg-gray-200'}`} />
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* SROI Section */}
        {sroi && (
          <Card data-testid="sroi-result">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base"><Zap className="h-5 w-5 text-amber-700" /> SROI Analysis</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-700">Investment (Inputs)</p>
                  <span className="block text-2xl font-bold text-gray-800">{formatCurrency(sroi.inputs_cost)}</span>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-gray-700">Social Value (Net)</p>
                  <span className="block text-2xl font-bold text-green-700">{formatCurrency(sroi.net_value)}</span>
                </div>
                <div className="text-center p-4 bg-amber-50 rounded-lg">
                  <p className="text-sm text-gray-700">SROI Ratio</p>
                  <span className="block text-2xl font-bold text-amber-700">{sroi.sroi_ratio}:1</span>
                  <p className="text-xs text-gray-700 mt-1">{sroi.sroi_ratio >= 1.5 ? 'Meets threshold (>=1.5)' : 'Below threshold (1.5)'}</p>
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">Outcome Breakdown</p>
                {(sroi.outcomes || []).map((o, i) => (
                  <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                    <div>
                      <span className="font-medium text-gray-800">{o.name}</span>
                      <span className="text-gray-700 ml-2">({o.quantity} × {formatCurrency(o.value_per_unit)})</span>
                    </div>
                    <span className="font-medium text-gray-700">{formatCurrency(o.gross_value)}</span>
                  </div>
                ))}
                <div className="flex justify-between text-xs text-gray-700 pt-2 border-t">
                  <span>Deadweight: {sroi.deadweight_pct}% | Attribution: {sroi.attribution_pct}%</span>
                  <span>Gross total: {formatCurrency(sroi.total_outcomes_value)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Theory of Change */}
        <TheoryOfChange evalId={selectedEval.id} readOnly={selectedEval.status === 'archived'} />

        {selectedEval.notes && (
          <Card><CardContent className="p-4"><p className="text-sm text-gray-700"><strong>Notes:</strong> {selectedEval.notes}</p></CardContent></Card>
        )}

        {/* Scoring Dialog (lifted into the detail view so the Edit Scores button can open it) */}
        <Dialog open={showScoringDialog} onOpenChange={setShowScoringDialog}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Score Evaluation: {selectedEval?.investment_title}</DialogTitle></DialogHeader>
            <div className="space-y-6 pt-2">
              {dimensions.map(dim => {
                const Icon = DIM_ICONS[dim.id] || Target;
                return (
                  <div key={dim.id}>
                    <div className="flex items-center gap-2 mb-3">
                      <Icon className="h-5 w-5" style={{ color: dim.color }} />
                      <h3 className="font-semibold text-gray-900">{dim.title}</h3>
                      <Badge variant="outline" className="text-xs">{dim.weight * 100}%</Badge>
                    </div>
                    <div className="space-y-3 ml-7">
                      {dim.criteria.map(c => (
                        <div key={c.id} className="border rounded-lg p-3">
                          <p className="text-sm font-medium text-gray-800">{c.title}</p>
                          <p className="text-xs text-gray-700 mb-2">{c.description}</p>
                          <div className="flex items-center gap-4">
                            <div className="flex gap-1">
                              {[1, 2, 3, 4, 5].map(n => (
                                <button key={n} onClick={() => setPendingScores({ ...pendingScores, [c.id]: { ...pendingScores[c.id], score: n } })}
                                  className={`w-8 h-8 rounded-full text-sm font-medium transition-colors ${pendingScores[c.id]?.score >= n ? 'bg-amber-400 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                                  data-testid={`score-${c.id}-${n}`}>
                                  {n}
                                </button>
                              ))}
                            </div>
                            <Input placeholder="Justification..." className="flex-1 text-xs h-8" value={pendingScores[c.id]?.justification || ''}
                              onChange={(e) => setPendingScores({ ...pendingScores, [c.id]: { ...pendingScores[c.id], justification: e.target.value } })} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowScoringDialog(false)}>Cancel</Button>
                <Button onClick={handleSubmitScores} className="bg-blue-600 hover:bg-blue-700" data-testid="save-scores-btn">Save Scores</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* SROI Dialog (lifted into the detail view) */}
        <Dialog open={showSROIDialog} onOpenChange={setShowSROIDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader><DialogTitle>SROI Calculator</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Investment Description</Label><Input value={sroiForm.inputs_description} onChange={(e) => setSroiForm({ ...sroiForm, inputs_description: e.target.value })} placeholder="Total project investment" /></div>
                <div><Label>Investment Cost (EUR)</Label><Input type="number" value={sroiForm.inputs_cost} onChange={(e) => setSroiForm({ ...sroiForm, inputs_cost: e.target.value })} placeholder="50000" data-testid="sroi-cost-input" /></div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Social Outcomes</Label>
                  <Button size="sm" variant="outline" onClick={addSROIOutcome}><Plus className="h-3 w-3 mr-1" /> Add</Button>
                </div>
                {sroiForm.outcomes.map((o, i) => (
                  <div key={i} className="border rounded-lg p-3 mb-2 space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <Input placeholder="Outcome name" value={o.name} onChange={(e) => updateSROIOutcome(i, 'name', e.target.value)} />
                      <Input placeholder="Financial proxy" value={o.financial_proxy} onChange={(e) => updateSROIOutcome(i, 'financial_proxy', e.target.value)} />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input type="number" placeholder="Quantity" value={o.quantity} onChange={(e) => updateSROIOutcome(i, 'quantity', e.target.value)} />
                      <Input type="number" placeholder="Value per unit (EUR)" value={o.value_per_unit} onChange={(e) => updateSROIOutcome(i, 'value_per_unit', e.target.value)} />
                    </div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Deadweight %</Label><Input type="number" value={sroiForm.deadweight_pct} onChange={(e) => setSroiForm({ ...sroiForm, deadweight_pct: e.target.value })} /></div>
                <div><Label>Attribution %</Label><Input type="number" value={sroiForm.attribution_pct} onChange={(e) => setSroiForm({ ...sroiForm, attribution_pct: e.target.value })} /></div>
              </div>
              <div className="flex justify-end gap-3 pt-2 border-t">
                <Button variant="outline" onClick={() => setShowSROIDialog(false)}>Cancel</Button>
                <Button onClick={handleSubmitSROI} className="bg-amber-700 hover:bg-amber-800" data-testid="calc-sroi-submit"><Calculator className="h-4 w-4 mr-1" /> Calculate SROI</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ========== LIST VIEW ==========

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard-btn"><ArrowLeft className="h-4 w-4 mr-2" /> Dashboard</Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><ClipboardCheck className="h-7 w-7 text-blue-600" /> Investment Evaluation</h1>
            <p className="text-gray-700 mt-1">Evaluate social investments across design, implementation, and completion phases</p>
          </div>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="new-evaluation-btn"><Plus className="h-4 w-4 mr-2" /> New Evaluation</Button>
      </div>

      {/* Main Tabs: Evaluations vs Benchmarks */}
      <Tabs value={mainTab} onValueChange={setMainTab}>
        <TabsList>
          <TabsTrigger value="evaluations" data-testid="tab-evaluations"><ClipboardCheck className="h-4 w-4 mr-1.5" /> Evaluations</TabsTrigger>
          <TabsTrigger value="benchmarks" data-testid="tab-benchmarks"><BarChart3 className="h-4 w-4 mr-1.5" /> Benchmarks & Comparison</TabsTrigger>
        </TabsList>

        <TabsContent value="evaluations">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
            <Card><CardContent className="p-4 text-center"><span className="block text-2xl font-bold text-blue-700">{stats.total || 0}</span><p className="text-xs text-gray-700">Total Evaluations</p></CardContent></Card>
            <Card><CardContent className="p-4 text-center"><span className="block text-2xl font-bold text-blue-700">{stats.by_stage?.design || 0}</span><p className="text-xs text-gray-700">Design Phase</p></CardContent></Card>
            <Card><CardContent className="p-4 text-center"><span className="block text-2xl font-bold text-amber-700">{stats.by_stage?.implementation || 0}</span><p className="text-xs text-gray-700">Implementation</p></CardContent></Card>
            <Card><CardContent className="p-4 text-center"><span className="block text-2xl font-bold text-green-700">{stats.by_stage?.completion || 0}</span><p className="text-xs text-gray-700">Completion</p></CardContent></Card>
            <Card className="bg-amber-50/40"><CardContent className="p-4 text-center"><span className="block text-2xl font-bold text-amber-700">{stats.avg_score || '—'}</span><p className="text-xs text-gray-700">Avg Score / 5</p></CardContent></Card>
          </div>

          {/* Filter */}
          <div className="flex gap-2 mb-4">
            {['all', 'design', 'implementation', 'completion'].map(f => (
              <Button key={f} variant={stageFilter === f ? 'default' : 'outline'} size="sm" onClick={() => setStageFilter(f)} className={stageFilter === f ? 'bg-gray-900' : ''} data-testid={`filter-${f}`}>
                {f === 'all' ? 'All Stages' : STAGE_CONFIG[f]?.label}
              </Button>
            ))}
          </div>

          {/* Evaluation List */}
          <section aria-labelledby="investment-evaluations-heading">
            <h2 id="investment-evaluations-heading" className="sr-only">Investment evaluations list</h2>
          {filtered.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16">
                <ClipboardCheck className="h-12 w-12 text-gray-400 mb-4" aria-hidden="true" />
                <h3 className="text-lg font-semibold text-gray-800 mb-2">No Evaluations Yet</h3>
                <p className="text-gray-700 text-center max-w-md mb-4">Create your first evaluation or load sample data.</p>
                <div className="flex gap-3">
                  <Button onClick={() => setShowCreateDialog(true)} className="bg-blue-700 hover:bg-blue-800"><Plus className="h-4 w-4 mr-2" aria-hidden="true" /> New Evaluation</Button>
                  {(user?.role === 'administrator' || user?.role === 'project_manager') && (
                    <Button variant="outline" onClick={handleSeed} data-testid="seed-eval-btn">Load Sample Data</Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {filtered.map(ev => (
                <Card key={ev.id} data-testid={`eval-${ev.id}`} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => handleViewEval(ev.id)}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={STAGE_CONFIG[ev.stage]?.color}>{STAGE_CONFIG[ev.stage]?.label}</Badge>
                          <Badge variant="outline" className="capitalize text-xs">{ev.status}</Badge>
                          {ev.sroi && <Badge className="bg-amber-100 text-amber-800 text-xs">SROI: {ev.sroi.sroi_ratio}:1</Badge>}
                          {ev.theory_of_change && (ev.theory_of_change.inputs || []).length > 0 && (
                            <Badge className="bg-indigo-100 text-indigo-700 text-xs">ToC</Badge>
                          )}
                        </div>
                        <h3 className="font-semibold text-gray-900">{ev.investment_title}</h3>
                        <p className="text-xs text-gray-700 mt-1">by {ev.evaluator_name} · {new Date(ev.created_at).toLocaleDateString('en-GB')}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-center w-20">
                          <span className="block text-2xl font-bold text-blue-700">{ev.weighted_total ? Number(ev.weighted_total).toFixed(2) : '—'}</span>
                          <p className="text-xs text-gray-700">/ 5.00</p>
                        </div>
                        <Eye className="h-5 w-5 text-gray-700" aria-hidden="true" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          </section>
        </TabsContent>

        <TabsContent value="benchmarks">
          <EvaluationBenchmark evaluations={evaluations} />
        </TabsContent>
      </Tabs>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>New Evaluation</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            {investments.length > 0 ? (
              <div>
                <Label>Social Investment</Label>
                <Select value={newEval.investment_id} onValueChange={(v) => { const inv = investments.find(i => i.id === v); setNewEval({ ...newEval, investment_id: v, investment_title: inv?.title || '' }); }}>
                  <SelectTrigger data-testid="select-investment"><SelectValue placeholder="Select an investment..." /></SelectTrigger>
                  <SelectContent>{investments.map(inv => <SelectItem key={inv.id} value={inv.id}>{inv.title}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ) : (
              <div><Label>Investment Title</Label><Input value={newEval.investment_title} onChange={(e) => setNewEval({ ...newEval, investment_title: e.target.value })} placeholder="Enter investment title" data-testid="eval-title-input" /></div>
            )}
            <div>
              <Label>Evaluation Stage</Label>
              <Select value={newEval.stage} onValueChange={(v) => setNewEval({ ...newEval, stage: v })}>
                <SelectTrigger data-testid="select-stage"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="design">Design Phase</SelectItem>
                  <SelectItem value="implementation">Implementation Phase</SelectItem>
                  <SelectItem value="completion">Completion Phase</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Notes</Label><Textarea value={newEval.notes} onChange={(e) => setNewEval({ ...newEval, notes: e.target.value })} rows={2} placeholder="Optional notes..." /></div>
            <div className="flex justify-end gap-3"><Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button><Button onClick={handleCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-eval-btn">Create</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Scoring + SROI dialogs are mounted in the detail-view return so the
          Edit Scores / SROI buttons (which live in the detail view) can open them. */}
    </div>
  );
};

export default InvestmentEvaluation;
